import React, { useState, useEffect } from "react";
import { useSelector, shallowEqual } from "react-redux";
import { fetchChampions } from "constants/gameDefaults";
import { motion } from "framer-motion";
import RoleFilter from "components/RoleFilter";
import { Tooltip } from "react-tooltip";
import useSound from "use-sound";
import pickSound from "styles/audio/pick.ogg";

function ChampionEdit({
  selectedForReplacement,
  onSelectChampion,
  onSeeResults,
}) {
  const game = useSelector((state) => state.gameReducer.game, shallowEqual);
  const isMuted = useSelector((state) => state.audioReducer.isMuted);
  const [playPickSound] = useSound(pickSound);

  const initialChampionsList = fetchChampions(game?.mode);
  const [filteredChampionsList, setFilteredChampionsList] = useState(new Map());

  useEffect(() => {
    setFilteredChampionsList(initialChampionsList);
  }, []);

  const isChampionPicked = (championId) =>
    game.bluePickedChampions.some((p) => p.championId === championId) ||
    game.redPickedChampions.some((p) => p.championId === championId);

  const isSelectedForReplacement = (championId) => {
    if (!selectedForReplacement) return false;
    const { side, index } = selectedForReplacement;
    const arr =
      side === "blue" ? game.bluePickedChampions : game.redPickedChampions;
    return arr[index]?.championId === championId;
  };

  const renderChampionButton = (champion, index) => {
    const championInList = initialChampionsList.get(champion.id);
    if (!championInList) return null;
    if (champion.id === "NoBan") return null;

    const isPicked = isChampionPicked(champion.id);
    const isBeingReplaced = isSelectedForReplacement(champion.id);

    // Greyed out if already picked AND not the one being replaced
    const isDisabled = isPicked && !isBeingReplaced;

    return (
      <React.Fragment key={`edit-champion${index}`}>
        <motion.div
          className="championPickerCard left"
          id={`edit-champion${index}`}
          data-tooltip-id={`edit-champion${index}`}
          data-tooltip-content={champion.name}
          onClick={() => {
            if (!isDisabled && selectedForReplacement) {
              if (!isMuted) playPickSound();
              onSelectChampion(champion.id);
            }
          }}
          style={{
            cursor:
              !isDisabled && selectedForReplacement ? "pointer" : "default",
          }}
        >
          <img
            src={champion.icon}
            draggable={false}
            alt={`${champion.name}Icon`}
            style={{
              objectFit: "cover",
              filter: isDisabled ? "grayscale(100%)" : "none",
            }}
          />
        </motion.div>
        <Tooltip id={`edit-champion${index}`} />
      </React.Fragment>
    );
  };

  return (
    <section className="Draft championPickerContainer ChampionEdit">
      <RoleFilter
        initialChampionsList={initialChampionsList}
        setFilteredChampionsList={setFilteredChampionsList}
      />
      <div className="championPickerWrapper">
        {[...filteredChampionsList.values()].map((champion, index) =>
          renderChampionButton(champion, index),
        )}
      </div>
      <div className="editBottomBar">
        <div className="editHint">
          {!selectedForReplacement
            ? "Click a champion on the sides to replace"
            : "Pick a replacement from the grid"}
        </div>
      </div>
      <section className="changeChampionsContainer">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onSeeResults}
          className="changeChampionsButton"
        >
          See Results
        </motion.button>
      </section>
    </section>
  );
}

export default ChampionEdit;
